import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import {
  allTimezones,
  channelOptions,
  countryOptions,
  daysOfWeek,
  allUsers,
  priorityOptions,
  leadSourceOptions,
  requirementEngagementOptions,
  requirementTypeOptions,
} from '../../constants';
import { IRequirement, IResource } from '../../types/brand';
import { addRequirement } from '../../hooks/mutations';
import { useClickOutside } from '../../hooks/useClickOutside';
import { requirementFormSchema } from '../../validations/requirement';
import Select from '../common/Select';
import { BtnPrimary } from '../common/Buttons';
import { LoaderCircle } from 'lucide-react';
import FileUpload from '../common/FileUpload';
import { uploadAsset } from '../../api/aws/aws';

// Reusable Text Input
interface TextInputProps {
  label: string;
  name: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
}
const TextInput: React.FC<TextInputProps> = ({
  label,
  name,
  value,
  onChange,
  type = 'text',
  placeholder,
  required = false,
}) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={name} className="font-medium text-sm text-gray-700">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="input border rounded-lg p-2 text-base"
    />
  </div>
);

// Reusable Select Input
interface SelectInputProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: { label: string; value: string }[];
  placeholder?: string;
  required?: boolean;
}
const SelectInput: React.FC<SelectInputProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  required = false,
}) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={name} className="font-medium text-sm text-gray-700">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    <select
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="input border rounded-lg p-2 text-base"
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

// Checkbox group for multiple selections
interface CheckboxGroupProps {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
  capitalize?: boolean;
  required?: boolean;
}
const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  label,
  options,
  selected,
  onToggle,
  capitalize = false,
  required = false,
}) => (
  <div className="flex flex-col gap-2">
    <label className="font-medium text-sm text-gray-700">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    <div className="flex flex-wrap gap-3">
      {options.map((opt) => {
        const display = capitalize
          ? opt.charAt(0).toUpperCase() + opt.slice(1)
          : opt;
        return (
          <label
            key={opt}
            className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selected.includes(opt)}
              onChange={() => onToggle(opt)}
              className="accent-black"
            />
            {display}
          </label>
        );
      })}
    </div>
  </div>
);

// MultiSelectInput for Channels
interface MultiSelectInputProps {
  label: string;
  options: { label: string; value: string }[];
  selected: string[];
  onChange: (value: string) => void;
  required?: boolean;
}
const MultiSelectInput: React.FC<MultiSelectInputProps> = ({
  label,
  options,
  selected,
  onChange,
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  useClickOutside(containerRef, closeDropdown, isOpen);

  return (
    <div ref={containerRef} className="relative flex flex-col gap-1.5">
      <label className="font-medium text-sm text-gray-700">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <div
        className="input border rounded-lg p-2 text-base cursor-pointer select-none"
        onClick={() => setIsOpen((prev) => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') setIsOpen((prev) => !prev);
        }}
      >
        {selected.length > 0
          ? selected.join(', ')
          : `Select ${label.toLowerCase()}`}
      </div>

      {isOpen && (
        <div className="absolute top-full mt-2 border border-gray-300 rounded-md shadow-md bg-white p-3 z-10 w-full max-w-md max-h-60 overflow-auto">
          {options.map(({ value, label }) => (
            <label
              key={value}
              className="flex items-center gap-2 mb-2 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(value)}
                onChange={() => onChange(value)}
                className="accent-black"
              />
              {label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

interface RequirementProps {
  onSuccess: () => void;
  onError: (error: Error) => void;
}

const Requirement: React.FC<RequirementProps> = ({ onSuccess, onError }) => {
  const navigate = useNavigate();
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [form, setForm] = useState<IRequirement>({
    clientName: '',
    clientPOC: '',
    website: '',
    email: '',
    country: '',
    expectedHiringBy: '',
    days: [],
    workingStart: '',
    workingEnd: '',
    priority: '',
    engagement: '',
    tickets: 0,
    comments: '',
    raisedBy: '',
    leadSource: '',
    timezone: '',
    languages: ['English'],
    channels: [],
    tools: [],
    attachments: [],
    status: 'active',
    type: 'customer-support',
  });

  const { mutate: addRequirementMutation, isPending } = addRequirement({
    onSuccess: (data) => {
      onSuccess();
      navigate(`/clients/rcse/${data?.data?._id}`);
    },
    onError: (e) => {
      console.error(e);
      onError(e);
    },
  });

  const updateField = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'tickets' ? Number(value) : value,
    }));
  };

  const toggleDay = (day: string) => {
    setForm((prev) => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day],
    }));
  };

  const addLanguage = (lang: string) => {
    if (!lang.trim()) return;
    if (!form.languages.includes(lang.trim())) {
      setForm((prev) => ({
        ...prev,
        languages: [...prev.languages, lang.trim()],
      }));
    }
  };

  const removeLanguage = (lang: string) => {
    setForm((prev) => ({
      ...prev,
      languages: prev.languages.filter((l) => l !== lang),
    }));
  };

  const toggleChannel = (channel: string) => {
    setForm((prev) => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter((c) => c !== channel)
        : [...prev.channels, channel],
    }));
  };

  const addTool = (tool: string) => {
    const normalizedTool = tool.trim();
    if (!normalizedTool) return;

    if (!form.tools.includes(normalizedTool)) {
      setForm((prev) => ({
        ...prev,
        tools: [...prev.tools, normalizedTool],
      }));
    }
  };

  const removeTool = (tool: string) => {
    setForm((prev) => ({
      ...prev,
      tools: prev.tools.filter((item) => item !== tool),
    }));
  };

  const uploadAttachments = async (): Promise<IResource[]> => {
    setIsUploadingAttachments(true);

    try {
      const uploadedAttachments = await Promise.all(
        attachmentFiles.map(async (file) => ({
          originalName: file.name,
          fileKey: await uploadAsset(file, 'APPLICANTS', 'requirements'),
          uploadedAt: new Date(),
        }))
      );

      return uploadedAttachments;
    } finally {
      setIsUploadingAttachments(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationResult = requirementFormSchema.safeParse({
      ...form,
      attachmentCount: attachmentFiles.length,
    });

    if (!validationResult.success) {
      toast.error(validationResult.error.issues[0]?.message || 'Invalid form');
      return;
    }

    try {
      const attachments = await uploadAttachments();
      addRequirementMutation({
        ...form,
        attachments,
      });
    } catch (error) {
      console.error(error);
      toast.error('Failed to upload attachments');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Text Inputs */}
        <TextInput
          label="Client Business Name"
          name="clientName"
          value={form.clientName}
          onChange={updateField}
          required
        />
        <TextInput
          label="Client POC"
          name="clientPOC"
          value={form.clientPOC}
          onChange={updateField}
          required
        />
        <TextInput
          label="Website"
          name="website"
          value={form.website}
          onChange={updateField}
          required
        />
        <TextInput
          label="Email"
          name="email"
          value={form.email}
          onChange={updateField}
          type="email"
          required
        />
        <SelectInput
          label="Engagement Type"
          name="engagement"
          value={String(form.engagement)}
          onChange={updateField}
          options={requirementEngagementOptions}
          placeholder="Select engagement"
          required
        />
        <TextInput
          label="Ticket Count / Day"
          name="tickets"
          value={form.tickets}
          onChange={updateField}
          type="number"
          required
        />

        {/* Country Select */}
        <div>
          <label
            htmlFor="country"
            className="font-medium text-sm text-gray-700"
          >
            Country
            <span className="text-red-500"> *</span>
          </label>
          <Select
            id="country"
            options={countryOptions}
            placeholder="Select country"
            value={
              countryOptions.find((opt) => opt.value === form.country) || null
            }
            onChange={(opt) =>
              setForm((prev) => ({ ...prev, country: opt?.value || '' }))
            }
          />
        </div>

        {/* Select Inputs */}
        <SelectInput
          label="Type"
          name="type"
          value={form.type}
          onChange={updateField}
          options={requirementTypeOptions}
          required
        />
        <SelectInput
          label="Priority"
          name="priority"
          value={form.priority}
          onChange={updateField}
          options={priorityOptions}
          placeholder="Select Priority"
        />
        <SelectInput
          label="Timezone"
          name="timezone"
          value={form.timezone}
          onChange={updateField}
          options={allTimezones}
          placeholder="Select Timezone"
          required
        />

        {/* Channels */}
        <MultiSelectInput
          label="Channels"
          options={channelOptions}
          selected={form.channels}
          onChange={toggleChannel}
          required
        />

        <div className="flex flex-col gap-1.5">
          <label className="font-medium text-sm text-gray-700">
            Tools
            <span className="text-red-500"> *</span>
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {form.tools.map((tool) => (
              <span
                key={tool}
                className="bg-gray-200 px-2 py-1 rounded-full text-sm flex items-center"
              >
                {tool}
                <button
                  type="button"
                  onClick={() => removeTool(tool)}
                  className="ml-1 text-red-500 font-bold"
                  aria-label={`Remove tool ${tool}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <input
            type="text"
            placeholder="Add tool + Enter"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTool(e.currentTarget.value);
                e.currentTarget.value = '';
              }
            }}
            className="input border rounded-lg p-2 text-base"
          />
        </div>

        {/* Days */}
        <CheckboxGroup
          label="Working Days"
          options={daysOfWeek}
          selected={form.days}
          onToggle={toggleDay}
          capitalize
          required
        />

        {/* Working Hours */}
        <TextInput
          label="Working Start"
          name="workingStart"
          value={form.workingStart}
          onChange={updateField}
          type="time"
          required
        />
        <TextInput
          label="Working End"
          name="workingEnd"
          value={form.workingEnd}
          onChange={updateField}
          type="time"
          required
        />

        {/* Expected Hiring By */}
        <TextInput
          label="Expected Hiring By"
          name="expectedHiringBy"
          value={form.expectedHiringBy}
          onChange={updateField}
          type="date"
        />

        {/* Languages */}
        <div className="flex flex-col gap-1.5">
          <label className="font-medium text-sm text-gray-700">Languages</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {form.languages.map((lang) => (
              <span
                key={lang}
                className="bg-gray-200 px-2 py-1 rounded-full text-sm flex items-center"
              >
                {lang}
                <button
                  type="button"
                  onClick={() => removeLanguage(lang)}
                  className="ml-1 text-red-500 font-bold"
                  aria-label={`Remove language ${lang}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <input
            type="text"
            placeholder="Add language + Enter"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addLanguage(e.currentTarget.value);
                e.currentTarget.value = '';
              }
            }}
            className="input border rounded-lg p-2 text-base"
          />
        </div>
      </div>

      {/* Comments */}
      <FileUpload
        title="Attachments"
        files={attachmentFiles}
        onFilesChange={setAttachmentFiles}
        required
        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        multiple={false}
        maxFiles={1}
        isUploading={isUploadingAttachments}
      />

      <div className="flex flex-col gap-1.5">
        <label htmlFor="comments" className="font-medium text-sm text-gray-700">
          Comments
        </label>
        <textarea
          id="comments"
          name="comments"
          value={form.comments}
          onChange={updateField}
          placeholder="Any additional info..."
          className="input border rounded-lg p-2 text-base h-24"
        />
      </div>

      {/* Raised By & Lead Source */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SelectInput
          label="Raised By"
          name="raisedBy"
          value={form.raisedBy}
          onChange={updateField}
          options={allUsers}
          placeholder="Raised By"
          required
        />
        <SelectInput
          label="Lead Source"
          name="leadSource"
          value={form.leadSource}
          onChange={updateField}
          options={leadSourceOptions}
          placeholder="Lead Source"
          required
        />
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <BtnPrimary
          type="submit"
          disabled={isPending || isUploadingAttachments}
        >
          {isPending || isUploadingAttachments ? (
            <div className="flex items-center gap-2">
              <span>{isUploadingAttachments ? 'Uploading' : 'Creating'}</span>
              <LoaderCircle size={16} className="animate-spin" />
            </div>
          ) : (
            'Create'
          )}
        </BtnPrimary>
      </div>
    </form>
  );
};

export default Requirement;
